/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Case, Switch } from "react-if";
import { Box } from "@nordcloud/gnui";
import { BreadcrumbsBox, Granularity } from "~/components";
import { EstatePeriod } from "~/constants";
import { useQueryState } from "~/hooks";
import {
  EstateGroupBy,
  EstateFiltersAndChartParams,
  EstateChartsQueryState,
} from "./components";
import { useQueryParametersValidation } from "./components/hooks";
import { EstateRecordsAccountsView } from "./EstateRecordsAccountsView";
import { EstateRecordsAccumulatedView } from "./EstateRecordsAccumulatedView";
import { EstateRecordsApplicationsView } from "./EstateRecordsApplicationsView";
import { EstateRecordsCategoryView } from "./EstateRecordsCategoryView";
import { EstateRecordsEnvironmentTypeView } from "./EstateRecordsEnvironmentTypeView";
import { EstateRecordsOrgUnitView } from "./EstateRecordsOrgUnitView";
import { EstateRecordsProviderView } from "./EstateRecordsProviderView";
import { EstateRecordsRegionView } from "./EstateRecordsRegionView";
import { EstateRecordsServiceView } from "./EstateRecordsServiceView";
import { EstateRecordsTagsView } from "./EstateRecordsTagsView";

const BREADCRUMBS = [
  {
    value: "estate-records",
    label: "Estate Records",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

export function EstateRecordsPage() {
  const { activeChart, preSelectedMonth } = useQueryParametersValidation();

  const [isDataLoading, setIsDataLoading] = useState(false);
  const {
    state: { estatePeriod, granularity },
  } = useQueryState<EstateChartsQueryState>();

  const period = estatePeriod ?? EstatePeriod.DEFAULT_MONTH;
  const chartGranularity = granularity ?? Granularity.MONTHS;

  const isChartVisible = (chart: EstateGroupBy) => chart === activeChart;

  return (
    <div>
      <BreadcrumbsBox
        title="Estate Records"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <EstateFiltersAndChartParams
          disableDailyView={false}
          disabledFilters={isDataLoading}
        />
        <Switch>
          <Case condition={isChartVisible(EstateGroupBy.NONE)}>
            <EstateRecordsAccumulatedView
              period={period}
              range={preSelectedMonth}
              granularity={chartGranularity}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.APP)}>
            <EstateRecordsApplicationsView
              granularity={chartGranularity}
              period={period}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ENV)}>
            <EstateRecordsEnvironmentTypeView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ORG)}>
            <EstateRecordsOrgUnitView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.PROVIDER)}>
            <EstateRecordsProviderView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.REGION)}>
            <EstateRecordsRegionView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.CATEGORY)}>
            <EstateRecordsCategoryView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ACCOUNT)}>
            <EstateRecordsAccountsView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.SERVICE)}>
            <EstateRecordsServiceView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.TAG)}>
            <EstateRecordsTagsView
              period={period}
              granularity={chartGranularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
        </Switch>
      </Box>
    </div>
  );
}
